export const tearsheetsPaths = {
  create: 'create',
  view: 'view',
  viewReport: 'view/:reportId',
  builder: 'builder',
  archive: 'archive',
  branding: 'branding',
} as const;

export function withQuery(path: string, params: Record<string, string | undefined>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) qs.set(key, value);
  }
  const encoded = qs.toString();
  return encoded ? `${path}?${encoded}` : path;
}

export function tearsheetViewPath(reportId: string): string {
  return `${tearsheetsPaths.view}/${encodeURIComponent(reportId)}`;
}

export function isLiveTearsheetPath(pathname: string): boolean {
  const parts = pathname.split('/').filter(Boolean);
  const leaf = parts.at(-1);
  const parent = parts.at(-2);
  if (leaf === tearsheetsPaths.create || leaf === tearsheetsPaths.builder) return true;
  return parent === tearsheetsPaths.view;
}

export function reportIdFromPathname(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  const viewIndex = parts.indexOf(tearsheetsPaths.view);
  if (viewIndex < 0) return '';
  return decodeURIComponent(parts[viewIndex + 1] ?? '');
}

/** True for `/tearsheets` list (not create/view/builder/branding). */
export function isTearsheetsListPath(pathname: string): boolean {
  const parts = pathname.split('/').filter(Boolean);
  return parts.length === 1 && parts[0] === 'tearsheets';
}

/**
 * Replace search on an explicit pathname.
 * Prefer this over `setSearchParams`: RR's helper does `navigate("?" + …)` relative to the
 * current location, so a stale list sync can pollute create/view URLs after navigation.
 */
export function searchFromParams(params: URLSearchParams): string {
  const encoded = params.toString();
  return encoded ? `?${encoded}` : '';
}

/** Query string for a tearsheets sub-nav link — never carry create/view-only params onto Reports. */
export function tearsheetsTabSearch(to: string, fundId: string, currentSearch = ''): string {
  const current = new URLSearchParams(currentSearch.startsWith('?') ? currentSearch.slice(1) : currentSearch);
  const id = fundId || current.get('fundId') || '';
  const next = new URLSearchParams();
  if (id) next.set('fundId', id);
  if (id && current.get('itemKind') === 'managedAccount') next.set('itemKind', 'managedAccount');

  if (to === '.' || to === '') {
    const from = current.get('from');
    const toMonth = current.get('to');
    if (from) next.set('from', from);
    if (toMonth) next.set('to', toMonth);
    return searchFromParams(next);
  }

  // Create + builder need as-of for terms/live data / deferred layout-components; branding needs it to freeze a month.
  if (to === tearsheetsPaths.create || to === tearsheetsPaths.builder || to === tearsheetsPaths.branding) {
    const reportDate = current.get('reportDate');
    if (reportDate) next.set('reportDate', reportDate);
    return searchFromParams(next);
  }

  return searchFromParams(next);
}
